import React, { useEffect, useState } from 'react';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import { Button, makeStyles, TableHead, TableSortLabel, Tooltip } from '@material-ui/core';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import CircularProgress from '@material-ui/core/CircularProgress';
import { LoadingProgress } from '../ContentLoading';
import Skeleton from '@material-ui/lab/Skeleton';
import IconButton from '@material-ui/core/IconButton';
import EditIcon from '@material-ui/icons/EditOutlined';
import variables from '../../Sys/variable.scss';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { formatUiDateTime } from '../../utils/dateTime';
import StatusChip, { toneForStatus } from '../StatusChip';

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    position: 'relative',
    borderRadius: 12,
    border: '1px solid var(--dm-border, #e7ebf0)',
    overflowX: 'auto',
    boxShadow: 'none',
    backgroundColor: 'var(--contentColor, #fff)',
  },
  headerCell: {
    color: 'var(--dm-text-strong, #263548)',
    fontWeight: 700,
    fontSize: '0.75rem',
    padding: '14px 16px',
    whiteSpace: 'nowrap',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    borderBottom: '1px solid var(--dm-border, #e7ebf0)',
    backgroundColor: 'var(--dm-row-alt, #f8fafc)',
    '&.MuiTableCell-stickyHeader': {
      backgroundColor: 'var(--dm-row-alt, #f8fafc)',
    },
  },
  sortLabel: {
    color: 'var(--dm-text-strong, #263548) !important',
    '&:hover': {
      color: `${variables.primaryColor || theme.palette.primary.main} !important`,
    },
    '&$sortLabelActive': {
      color: `${variables.primaryColor || theme.palette.primary.main} !important`,
    },
  },
  sortLabelActive: {
    color: `${variables.primaryColor || theme.palette.primary.main} !important`,
  },
  sortIcon: {
    color: 'var(--dm-muted, #647184) !important',
    '$sortLabelActive &': {
      color: `${variables.primaryColor || theme.palette.primary.main} !important`,
    },
  },
  bodyRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: 'var(--dm-row-alt, #f8fafc)',
    },
    '&:nth-of-type(even)': {
      backgroundColor: 'var(--dm-row, #fff)',
    },
    '&:hover': {
      backgroundColor: 'rgba(61, 125, 221, 0.06) !important',
    },
    transition: 'background-color 0.15s ease',
    '&:last-child td': {
      borderBottom: 'none',
    },
  },
  bodyCell: {
    color: 'var(--dm-text-strong, #263548)',
    padding: '13px 16px',
    fontSize: '0.8125rem',
    fontWeight: 500,
    lineHeight: 1.45,
    borderBottom: '1px solid var(--dm-border, #e7ebf0)',
  },
  loadingCell: {
    padding: '40px 16px',
    textAlign: 'center',
  },
  loadingPlaceholder: {
    height: 220,
    padding: 0,
    borderBottom: 'none',
    backgroundColor: 'var(--dm-row-alt, #f8fafc)',
  },
  emptyRow: {
    '& td': {
      padding: '40px 16px',
      textAlign: 'center',
      color: 'var(--dm-muted, #647184)',
      fontSize: '0.875rem',
      fontWeight: 500,
      fontStyle: 'normal',
      borderBottom: 'none',
    },
  },
  editButton: {
    color: theme.palette.primary.main,
    background: 'rgba(61, 125, 221, 0.1)',
    boxShadow: 'none',
    padding: 8,
    borderRadius: 8,
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '&:hover': {
      background: 'rgba(61, 125, 221, 0.18)',
    },
  },
  /** Disabled IconButton strips background; keep same fill for loading spinner */
  editButtonLoading: {
    '&.Mui-disabled': {
      backgroundColor: 'rgba(61, 125, 221, 0.1)',
      opacity: 1,
      color: theme.palette.primary.main,
    },
  },
  actionGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: 8,
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },
  approveButton: {
    width: 34,
    height: 34,
    padding: '8px !important',
    borderRadius: 8,
    color: '#1f7a3f !important',
    backgroundColor: 'rgba(40, 167, 69, 0.12) !important',
    boxShadow: 'none',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(40, 167, 69, 0.2) !important',
    },
  },
  deleteButton: {
    width: 34,
    height: 34,
    padding: '8px !important',
    borderRadius: 8,
    color: '#c62828 !important',
    backgroundColor: 'rgba(245, 25, 25, 0.1) !important',
    boxShadow: 'none',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(245, 25, 25, 0.18) !important',
    },
  },
  enrollButton: {
    padding: '6px 16px',
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.75rem',
  },
  cellColumn: {
    minWidth: 168,
    wordBreak: 'break-all',
  },
  expandButton: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontWeight: 600,
  },
  tooltipCell: {
    cursor: 'pointer',
  },
  tooltipContent: {
    fontSize: '0.82rem',
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxWidth: 360,
  },
}));

function RenderButton({ item, rowItem, handleItemClick, actionLoadingRowId, actionLoadingRowField }) {
  const classes = useStyles();
  const rowId = actionLoadingRowField ? rowItem?.[actionLoadingRowField] : null;
  const isThisRowLoading = actionLoadingRowId != null && rowId != null && actionLoadingRowId === rowId;

  if (item === 'edit') {
    if (isThisRowLoading) {
      return (
        <IconButton
          size="small"
          className={`${classes.editButton} ${classes.editButtonLoading}`}
          disabled
          aria-label="Loading"
        >
          <CircularProgress size={18} thickness={4} color="inherit" />
        </IconButton>
      );
    }
    return (
      <IconButton size="small" className={classes.editButton} onClick={() => handleItemClick(rowItem, 'edit')} aria-label="Edit">
        <EditIcon fontSize="small" />
      </IconButton>
    );
  }
  if (item === 'enroll') {
    return (
      <Button variant="contained" className={classes.enrollButton} size="small" color="primary" onClick={() => handleItemClick(rowItem, 'edit')}>
        Enroll
      </Button>
    );
  }
  if (item === 'reject') {
    return (
      <IconButton size="small" className={classes.deleteButton} onClick={() => handleItemClick(rowItem, 'reject')} aria-label="Reject">
        <CancelIcon fontSize="small" />
      </IconButton>
    );
  }
  if (item === 'approve') {
    return (
      <IconButton size="small" className={classes.approveButton} onClick={() => handleItemClick(rowItem, 'approve')} aria-label="Approve">
        <CheckCircleIcon fontSize="small" />
      </IconButton>
    );
  }
  return null;
}

function RenderItem({ rowItem, colItem, handleItemClick, tooltipId, activeTooltipId, setActiveTooltipId, actionLoadingRowId, actionLoadingRowField }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const classes = useStyles();

  const handleToggleText = () => setIsExpanded((prev) => !prev);

  if (typeof colItem.render === 'function') {
    const rendered = colItem.render(rowItem);
    if (rendered == null || rendered === '') return '';
    return rendered;
  }

  const rawValue = rowItem[colItem.key];
  const displayValue = formatUiDateTime(rawValue);

  if (
    colItem.key === 'status'
    && ['string', 'number'].includes(typeof rawValue)
    && displayValue != null
    && displayValue !== ''
    && displayValue !== 'NA'
  ) {
    return <StatusChip tone={toneForStatus(displayValue)}>{String(displayValue).replace(/_/g, ' ')}</StatusChip>;
  }

  const renderLongText = (text) => {
    const words = text.split(' ');
    const truncatedText = words.slice(0, 80).join(' ');
    const fullText = words.join(' ');
    return (
      <div>
        {isExpanded ? fullText : truncatedText}
        {words.length > 80 && (
          <span onClick={handleToggleText} className={classes.expandButton}>
            {isExpanded ? ' less' : ' more'}
          </span>
        )}
      </div>
    );
  };

  if (colItem.tooltip && typeof displayValue === 'string' && displayValue) {
    const CHAR_LIMIT = 80;
    const fullText = displayValue;
    const isTruncated = fullText.length > CHAR_LIMIT;
    const displayText = isTruncated ? fullText.slice(0, CHAR_LIMIT) + '…' : fullText;
    const isTouchDevice = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(hover: none)').matches;
    const isTooltipOpen = activeTooltipId === tooltipId;

    const handleTooltipToggle = () => {
      if (!isTouchDevice || !isTruncated) return;
      if (isTooltipOpen) {
        setActiveTooltipId(null);
      } else {
        setActiveTooltipId(tooltipId);
      }
    };

    return (
      <Tooltip
        title={<span className={classes.tooltipContent}>{fullText}</span>}
        placement="top"
        arrow
        open={isTouchDevice ? isTooltipOpen : undefined}
        onClose={() => setActiveTooltipId(null)}
        disableHoverListener={isTouchDevice}
        disableFocusListener={isTouchDevice}
        disableTouchListener={isTouchDevice}
        enterTouchDelay={0}
        leaveTouchDelay={3000}
      >
        <span
          className={classes.tooltipCell}
          data-tooltip-trigger="true"
          data-tooltip-id={tooltipId}
          onClick={handleTooltipToggle}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') handleTooltipToggle();
          }}
          role="button"
          tabIndex={0}
        >
          {displayText}
        </span>
      </Tooltip>
    );
  }

  if (colItem.key === 'action' && colItem.actions && colItem.actions.length) {
    if (rowItem.status && (rowItem.status === 'declined' || rowItem.status === 'approved')) {
      return <StatusChip tone={toneForStatus(rowItem.status)}>{rowItem.status}</StatusChip>;
    }
    return (
      <div className={classes.actionGroup}>
        {colItem.actions.map((btn) => (
          <RenderButton
            key={btn}
            item={btn}
            handleItemClick={handleItemClick}
            rowItem={rowItem}
            actionLoadingRowId={actionLoadingRowId}
            actionLoadingRowField={actionLoadingRowField}
          />
        ))}
      </div>
    );
  }

  if (colItem.key === 'logo') {
    return rowItem?.logo && rowItem.logo !== '' ? (
      <img loading="lazy" decoding="async" src={rowItem.logo} alt="Logo" style={{ marginLeft: 10, width: 'auto', height: '50px', maxWidth: '50px', objectFit: 'contain' }} />
    ) : null;
  }

  if (colItem.key === 'link') {
    return rowItem?.link && rowItem.link !== '' ? (
      <a rel="noreferrer" href={rowItem.link} target="_blank">{rowItem.link}</a>
    ) : null;
  }

  if (typeof displayValue === 'string' && displayValue.split(' ').length > 80) {
    return renderLongText(displayValue);
  }

  if (Array.isArray(rowItem[colItem.key])) {
    return rowItem[colItem.key].length ? rowItem[colItem.key].join(', ') : '';
  }

  if (displayValue != null && displayValue !== 'NA') {
    return <>{displayValue}</>;
  }
  return '';
}

function GenericTable({
  columns,
  list,
  loading,
  handleItemClick,
  left,
  applyMinWidth = true,
  onSort,
  sortBy,
  sortOrder = 'asc',
  actionLoadingRowId = null,
  actionLoadingRowField = null,
}) {
  const classes = useStyles();
  const [activeTooltipId, setActiveTooltipId] = useState(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!activeTooltipId) return;
      const target = event.target;
      if (!target || !target.closest) return;

      const clickedTooltipContent = target.closest('[role="tooltip"]');
      const clickedTrigger = target.closest('[data-tooltip-trigger="true"]');
      const clickedTriggerId = clickedTrigger?.getAttribute('data-tooltip-id');

      if (clickedTooltipContent) return;
      if (clickedTriggerId === activeTooltipId) return;
      setActiveTooltipId(null);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [activeTooltipId]);

  const handleSortClick = (field) => {
    if (!onSort) return;
    const newDirection = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    onSort(field, newDirection);
  };

  const align = left ? 'left' : 'center';

  return (
    <TableContainer className={classes.tableContainer} aria-busy={Boolean(loading)}>
      {loading ? <LoadingProgress label="Loading table" /> : null}
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((col, index) => (
              <TableCell
                key={index}
                align={align}
                className={`${classes.headerCell} ${applyMinWidth ? classes.cellColumn : ''}`}
                style={{
                  ...((col.key === 'name' || col.key === 'firstName' || col.key === 'lastName' || col.key === 'userName' || col.key === 'location' || col.key === 'role') && { minWidth: 100 }),
                  ...((col.key === 'action' || col.key === 'actions') && { minWidth: 140, whiteSpace: 'nowrap' }),
                }}
              >
                {col.sortable && onSort ? (
                  <TableSortLabel
                    active={sortBy === col.key}
                    direction={sortBy === col.key ? sortOrder : 'asc'}
                    onClick={() => handleSortClick(col.key)}
                    classes={{
                      root: classes.sortLabel,
                      active: classes.sortLabelActive,
                      icon: classes.sortIcon,
                    }}
                  >
                    {col.label}
                  </TableSortLabel>
                ) : (
                  col.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            Array.from({ length: Math.min(list.length || 6, 6) }, (_, rowIndex) => (
              <TableRow key={rowIndex} className={classes.bodyRow} aria-hidden="true" data-testid="table-skeleton-row">
                {columns.map((column, columnIndex) => (
                  <TableCell key={columnIndex} className={classes.bodyCell} align={align}>
                    <Skeleton variant="text" width={column.key === 'action' ? 32 : '80%'} height={24} />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : list.length > 0 ? (
            list.map((rowItem, rowIndex) => (
              <TableRow key={rowIndex} className={classes.bodyRow}>
                {columns.map((colItem, colIndex) => (
                  <TableCell
                    key={colIndex}
                    align={align}
                    className={classes.bodyCell}
                    style={colItem.key === 'action' || colItem.key === 'actions' ? { whiteSpace: 'nowrap' } : undefined}
                  >
                    <RenderItem
                      rowItem={rowItem}
                      colItem={colItem}
                      handleItemClick={handleItemClick}
                      tooltipId={`${rowIndex}-${colItem.key}`}
                      activeTooltipId={activeTooltipId}
                      setActiveTooltipId={setActiveTooltipId}
                      actionLoadingRowId={actionLoadingRowId}
                      actionLoadingRowField={actionLoadingRowField}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow className={classes.emptyRow}>
              <TableCell colSpan={columns.length}>No records found.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default GenericTable;
